/** Platform sales (Foodsi / Too Good To Go / Pyszne) web-admin API (MD-0093). */

import { api } from '../client';

import type { StockDocumentMutationOptions } from './documentMutationCache';

export type PlatformSaleChannel = 'foodsi' | 'tgtg' | 'pyszne';
export type PlatformSaleStatus = 'draft' | 'confirmed';

export interface PlatformSaleListItem {
  id: string;
  channel: PlatformSaleChannel;
  location_id?: string | null;
  location_name?: string | null;
  /** ISO datetime (TIMESTAMPTZ). */
  sold_at: string;
  gopos_order_id?: string | null;
  order_number?: string | null;
  pack_count: number;
  /** Only on historical sales migrated from B2B orders; new sales carry revenue on the GoPOS receipt. */
  revenue_net?: string | null;
  status: PlatformSaleStatus;
  external_key?: string | null;
  notes?: string | null;
  current_revision_no?: number | null;
  line_count: number;
  updated_at?: string | null;
}

export interface PlatformSaleLine {
  id: string;
  line_ref: string;
  product_id: string;
  product_name?: string | null;
  quantity: string;
  unit_code: string;
  notes?: string | null;
}

export interface PlatformSale extends Omit<PlatformSaleListItem, 'line_count'> {
  legacy_b2b_order_id?: string | null;
  legacy_write_off_id?: string | null;
  created_at?: string | null;
  confirmed_at?: string | null;
  lines: PlatformSaleLine[];
}

export interface PlatformSaleFilters {
  [key: string]: string | number | undefined;
  /** Comma-separated location ids. */
  location_id?: string;
  /** Comma-separated channels. */
  channel?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface PlatformSaleListResponse {
  request_id: string;
  items: PlatformSaleListItem[];
  total: number;
}

export interface PlatformSaleDetailResponse {
  request_id: string;
  item: PlatformSale;
  replay?: boolean;
  already_confirmed?: boolean;
}

export interface PlatformSaleLineRequest {
  product_id: string;
  quantity: string;
  unit_code: string;
  notes?: string | null;
}

export interface PlatformSaleRequest {
  channel: PlatformSaleChannel;
  sold_at: string;
  gopos_order_id: string;
  location_id?: string | null;
  pack_count?: number;
  notes?: string | null;
  external_key?: string | null;
  lines: PlatformSaleLineRequest[];
}

export interface CreatePlatformSaleRequest extends PlatformSaleRequest {
  status?: PlatformSaleStatus;
}

export interface UpdatePlatformSaleRequest extends PlatformSaleRequest {
  change_reason?: string | null;
}

export interface PlatformSaleActionRequest {
  reason?: string | null;
}

type MutationOptions = Pick<StockDocumentMutationOptions, 'idempotencyKey' | 'expectedUpdatedAt'>;

/** Idempotency key plus optimistic concurrency via the shared `X-Expected-Updated-At` header. */
function mutationOptions(options?: MutationOptions): {
  idempotencyKey?: string;
  headers?: Record<string, string>;
} {
  const requestOptions: { idempotencyKey?: string; headers?: Record<string, string> } = {};
  if (options?.idempotencyKey) requestOptions.idempotencyKey = options.idempotencyKey;
  const expectedUpdatedAt = options?.expectedUpdatedAt?.trim();
  if (expectedUpdatedAt) requestOptions.headers = { 'X-Expected-Updated-At': expectedUpdatedAt };
  return requestOptions;
}

const BASE = '/stock/platform-sales';

export const platformSalesApi = {
  list: (filters: PlatformSaleFilters = {}): Promise<PlatformSaleListResponse> =>
    api.webAdmin.get<PlatformSaleListResponse>(BASE, { params: filters }),

  detail: (id: string): Promise<PlatformSaleDetailResponse> =>
    api.webAdmin.get<PlatformSaleDetailResponse>(`${BASE}/${id}`),

  create: (
    data: CreatePlatformSaleRequest,
    options?: MutationOptions,
  ): Promise<PlatformSaleDetailResponse> =>
    api.webAdmin.post<PlatformSaleDetailResponse>(BASE, data, mutationOptions(options)),

  update: (
    id: string,
    data: UpdatePlatformSaleRequest,
    options?: MutationOptions,
  ): Promise<PlatformSaleDetailResponse> =>
    api.webAdmin.put<PlatformSaleDetailResponse>(`${BASE}/${id}`, data, mutationOptions(options)),

  confirm: (id: string, options?: MutationOptions): Promise<PlatformSaleDetailResponse> =>
    api.webAdmin.post<PlatformSaleDetailResponse>(
      `${BASE}/${id}/confirm`,
      undefined,
      mutationOptions(options),
    ),

  /** Cancels the posting; the sale returns to draft. */
  cancel: (
    id: string,
    data: PlatformSaleActionRequest,
    options?: MutationOptions,
  ): Promise<PlatformSaleDetailResponse> =>
    api.webAdmin.post<PlatformSaleDetailResponse>(
      `${BASE}/${id}/cancel`,
      data,
      mutationOptions(options),
    ),

  delete: (
    id: string,
    data: PlatformSaleActionRequest,
    options?: MutationOptions,
  ): Promise<PlatformSaleDetailResponse> =>
    api.webAdmin.delete<PlatformSaleDetailResponse>(`${BASE}/${id}`, {
      ...mutationOptions(options),
      body: data,
    }),
};
